import { Checkbox } from 'components/Checkbox';
import { Stack } from 'components/Stack';
import { SystemLabel } from 'components/SystemLabel';

import { useDetectDevice } from 'hooks';
import { usePTPConfigContext } from 'GlobalConfig.context';
import { useRef } from 'react';
import { getRandomId } from 'utils/uid.utils';
import { useLineItem } from '../../hooks';
import type { LineItemProps } from '../../interfaces';

import { ProductImage, SIZES } from '../../../../../ProductImage';
import { CustomReasonInput } from '../CustomReasonInput';
import { ReasonForReturnInput } from '../ReasonForReturnInput';
import { ReturnQuantityInput } from '../ReturnQuantityInput';

import {
  CheckboxWrapper,
  Container,
  DetailsWrapper,
  ImageWrapper,
  LabelWrapper,
  OptionsWrapper,
  ReturnQuantityWrapper,
  ItemInfo,
  ProductName,
  ProductNameItem,
} from './LineItem.styles';

export const LineItem = (props: LineItemProps) => {
  const {
    returnable,
    selected,
    label,
    productData,
    returnData,
    errors,
    onSelectItem,
    onChangeReturnQuantity,
    onChangeReasonForReturn,
    onChangeCustomReasonForReturn,
  } = useLineItem(props);
  const ptpConfig = usePTPConfigContext();
  const { isMobile } = useDetectDevice();
  const productImageSize = isMobile ? SIZES.sm : SIZES.md;

  // Generate a unique ID for the product name to serve as label for the checkbox
  const { current: productRandId } = useRef(`product-${getRandomId()}`);
  const priceDisplay = productData.productPrice?.value || '';

  return (
    <Container hasLabel={!!label} disabled={!returnable}>
      {label && (
        <LabelWrapper>
          <SystemLabel color={label.color}>{label.text}</SystemLabel>
        </LabelWrapper>
      )}

      {returnable && (
        <CheckboxWrapper>
          <Checkbox
            checked={selected}
            onChange={(e) => onSelectItem(e.target.checked)}
            labelId={productRandId}
          />
        </CheckboxWrapper>
      )}

      {productData.productImageUrl && (
        <ImageWrapper>
          <ProductImage
            size={productImageSize}
            src={productData.productImageUrl}
            lineItemConfig={ptpConfig.lineItem}
            alt={productData.productName}
          />
        </ImageWrapper>
      )}

      <DetailsWrapper>
        <Stack direction="column" gap={8}>
          {isMobile ? (
            <>
              <ProductName size="sm" id={productRandId}>
                {productData.productName}
              </ProductName>
              {productData.productID && (
                <ItemInfo>
                  {productData.productID.label}: {productData.productID.value}
                </ItemInfo>
              )}
              <ItemInfo>{priceDisplay}</ItemInfo>
            </>
          ) : (
            <>
              <ProductName size="sm" id={productRandId}>
                <ProductNameItem>{productData.productName}</ProductNameItem>
                {returnable && <ItemInfo>{priceDisplay}</ItemInfo>}
              </ProductName>
              {productData.productID && (
                <ItemInfo>
                  {productData.productID.label}: {productData.productID.value}
                </ItemInfo>
              )}
            </>
          )}
          {productData.additionalInformation && (
            <ItemInfo>{productData.additionalInformation}</ItemInfo>
          )}
        </Stack>
      </DetailsWrapper>

      {selected && (
        <OptionsWrapper>
          <Stack direction="column" gap={8}>
            <Stack
              direction={{
                mobile: 'column',
                desktop: 'row',
              }}
              gap={{ mobile: 8, desktop: 10 }}
            >
              <ReturnQuantityWrapper>
                <ReturnQuantityInput
                  value={returnData.quantity}
                  lineItemQuantity={productData.quantity}
                  onChange={onChangeReturnQuantity}
                />
              </ReturnQuantityWrapper>
              <Stack flex="1">
                <ReasonForReturnInput
                  value={returnData.reason}
                  errorMessage={errors?.reason}
                  onChange={onChangeReasonForReturn}
                />
              </Stack>
            </Stack>
            {returnData.reason?.value === 'RETURN_REASON_OTHER' && (
              <CustomReasonInput
                value={returnData.customReason}
                errorMessage={errors?.customReason}
                onChange={onChangeCustomReasonForReturn}
              />
            )}
          </Stack>
        </OptionsWrapper>
      )}
    </Container>
  );
};
